/**
 * Customer chat bubble (Feb 2026 v2) — floating bottom-right on every
 * customer page. Opens a slide-over drawer with the full thread. Auto-
 * creates the thread on first message.
 *
 * v2 additions:
 *   • Date-day separators between messages
 *   • WhatsApp ✓✓ read ticks on outgoing customer messages
 *   • Live typing indicator ("Ash & Merch is typing…")
 *   • iOS-Glass audible chime on inbound dispatcher message (muteable)
 *   • Collapse/expand for long messages
 *   • SSE subscription — messages, typing, read receipts, and thread-deleted
 *     events now arrive live instead of only via 45s polling
 *   • Handles chat.thread_deleted (SA nuked the conversation) gracefully
 *
 * Note: customers CANNOT delete messages — this is a compliance decision
 * for a licensed CBD business (audit trail preservation).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Check, CheckCheck, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { useSSE } from '../hooks/useSSE';
import { playChime, isChatMuted, setChatMuted } from '../lib/chatSounds';
import { withDaySeparators, isLongMessage, formatShortTime } from '../lib/chatFormatting';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const POLL_MS = 45000;
const TYPING_TIMEOUT_MS = 5000;
const TYPING_THROTTLE_MS = 3000;

function MessageBody({ body }) {
  const [expanded, setExpanded] = useState(false);
  const long = isLongMessage(body);
  if (!long || expanded) {
    return (
      <>
        <p className="whitespace-pre-wrap break-words">{body}</p>
        {long && expanded && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[10px] underline mt-1 opacity-70 hover:opacity-100"
            data-testid="customer-chat-collapse"
          >
            Show less
          </button>
        )}
      </>
    );
  }
  const preview = body.slice(0, 220).replace(/\s+\S*$/, '') + '…';
  return (
    <>
      <p className="whitespace-pre-wrap break-words">{preview}</p>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="text-[10px] underline mt-1 opacity-70 hover:opacity-100"
        data-testid="customer-chat-expand"
      >
        Show more
      </button>
    </>
  );
}

function Ticks({ status }) {
  // WhatsApp semantics: single ✓ = sending, double ✓✓ grey = delivered,
  // double ✓✓ blue = read.
  const cls = status === 'read' ? 'text-[#3B9EFF]' : 'text-[#4B6B52]';
  const Icon = status === 'sending' ? Check : CheckCheck;
  return <Icon className={`w-3 h-3 inline-block ml-1 ${cls}`} aria-label={`message ${status}`} />;
}

export default function CustomerChatBubble({ user }) {
  const { isModern } = useTheme();
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [muted, setMuted] = useState(isChatMuted());
  const [typingAt, setTypingAt] = useState(0);
  const bottomRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/customer/chat`, { withCredentials: true });
      setThread(data.thread);
      setMessages(data.messages || []);
      setUnread(data.thread?.unread_customer_count || 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'customer') return undefined;
    load();
    const t = setInterval(load, POLL_MS);
    const openHandler = () => setOpen(true);
    window.addEventListener('openCustomerChat', openHandler);
    return () => {
      clearInterval(t);
      window.removeEventListener('openCustomerChat', openHandler);
    };
  }, [user, load]);

  // Prod safety net: SSE can be buffered by CDNs, so when the drawer is
  // open we also poll every 6 s. This makes ✓✓ read ticks + inbound
  // messages flip promptly even if the SSE socket is lagging.
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [open, load]);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      axios.post(`${API_URL}/api/customer/chat/read`, {}, { withCredentials: true }).catch(() => {});
      setUnread(0);
    }
  }, [open, messages.length, typingAt]);

  // Auto-clear typing indicator
  useEffect(() => {
    if (!typingAt) return undefined;
    const t = setTimeout(() => setTypingAt(0), TYPING_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [typingAt]);

  // ---- SSE: live message/typing/read/deleted ----
  const handleSSE = useCallback((evt) => {
    if (!evt?.type?.startsWith('chat.')) return;
    if (evt.type === 'chat.message') {
      if (!evt.message) return;
      // Append only if this is our thread (customer only has one)
      setMessages((prev) => (prev.some((m) => m.id === evt.message.id) ? prev : [...prev, evt.message]));
      if (evt.sender_role !== 'customer') {
        // inbound from Ash & Merch
        playChime();
        if (openRef.current) {
          // Auto-mark-read while drawer is open
          axios.post(`${API_URL}/api/customer/chat/read`, {}, { withCredentials: true }).catch(() => {});
        } else {
          setUnread((u) => u + 1);
        }
      }
    } else if (evt.type === 'chat.typing') {
      if (evt.sender_role !== 'customer') {
        setTypingAt(Date.now());
      }
    } else if (evt.type === 'chat.read') {
      // Dispatcher opened the thread — flip our outgoing ticks to "read".
      if (evt.viewer_role === 'dispatcher' || evt.viewer_role === 'super_admin') {
        const now = new Date().toISOString();
        setMessages((prev) => prev.map((m) => (m.sender_role === 'customer' && !m.read_at ? { ...m, read_at: now } : m)));
      }
    } else if (evt.type === 'chat.thread_deleted') {
      setMessages([]);
      setThread(null);
      setUnread(0);
      if (openRef.current) toast.info('This conversation was closed by support');
    }
  }, []);
  useSSE(handleSSE, !!user && user.role === 'customer');

  const notifyTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return;
    lastTypingSentRef.current = now;
    axios.post(`${API_URL}/api/customer/chat/typing`, {}, { withCredentials: true }).catch(() => {});
  }, []);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/customer/chat/messages`,
        { body },
        { withCredentials: true },
      );
      setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
      setDraft('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const toggleMute = () => {
    const m = !muted;
    setMuted(m);
    setChatMuted(m);
  };

  const messageItems = useMemo(() => withDaySeparators(messages), [messages]);
  const showTyping = typingAt > 0 && Date.now() - typingAt < TYPING_TIMEOUT_MS;

  if (!user || user.role !== 'customer') return null;

  return (
    <>
      {/* Floating bubble — stacked ABOVE the Strain Finder FAB (which
          sits at bottom-6 right-6 on the customer dashboard), so the two
          controls don't collide. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={isModern
          ? 'fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95'
          : 'fixed bottom-24 right-6 z-50 w-14 h-14 bg-[#39FF14] text-[#050003] rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform'}
        style={isModern ? { background: 'var(--am-emerald-600)', boxShadow: '0 8px 24px rgba(4,120,87,0.35)' } : undefined}
        data-testid="customer-chat-bubble"
        aria-label="Open chat"
      >
        <MessageCircle className="w-6 h-6" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-[#FF3B30] text-white text-xs font-bold rounded-full flex items-center justify-center"
            data-testid="customer-chat-unread-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-stretch sm:justify-end bg-black/60"
          onClick={() => setOpen(false)}
          data-testid="customer-chat-drawer"
        >
          <div
            className="w-full sm:w-[420px] sm:max-w-[90vw] bg-[#0C0612] border-l border-[#2A0F3D] flex flex-col h-[80vh] sm:h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[#2A0F3D]">
              <div>
                <p className="text-[#39FF14] font-mono text-sm font-bold uppercase tracking-wide">Chat with Ash &amp; Merch</p>
                <p className="text-[#4B6B52] font-mono text-[10px]">// we typically reply within minutes</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={toggleMute}
                  className={`p-1 border border-[#2A0F3D] ${muted ? 'text-[#4B6B52]' : 'text-[#39FF14]'} hover:border-[#39FF14]`}
                  title={muted ? 'Unmute chime' : 'Mute chime'}
                  data-testid="customer-chat-mute-toggle"
                  aria-label={muted ? 'Unmute chat sound' : 'Mute chat sound'}
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-1 text-[#A3C7A8] hover:text-[#39FF14]"
                  data-testid="customer-chat-close"
                  aria-label="Close chat"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="customer-chat-messages">
              {messageItems.length === 0 && (
                <p className="text-[#4B6B52] font-mono text-xs text-center py-8">// no messages yet. say hi 👋</p>
              )}
              {messageItems.map((item) => {
                if (item.__sep) {
                  return (
                    <div key={item.id} className="flex items-center gap-2 my-4" data-testid={`customer-chat-day-sep-${item.label}`}>
                      <div className="flex-1 h-px bg-[#2A0F3D]" />
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#4B6B52] px-2">{item.label}</span>
                      <div className="flex-1 h-px bg-[#2A0F3D]" />
                    </div>
                  );
                }
                const m = item;
                // Track A — system messages (driver arrival etc.) render as a
                // centered info banner, not a green/purple chat bubble. Clear
                // signal that this is automated and not part of the human
                // back-and-forth.
                if (m.sender_role === 'system') {
                  return (
                    <div key={m.id} className="flex justify-center my-2" data-testid={`customer-chat-sys-${m.id}`}>
                      <div className="max-w-[90%] px-3 py-2 border border-[#2A0F3D] bg-[rgba(180,0,255,0.06)] font-mono text-xs text-[#E4CCFF] text-center">
                        <p className="whitespace-pre-wrap">{m.body}</p>
                        <p className="text-[9px] mt-1 opacity-50">{formatShortTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                }
                const mine = m.sender_role === 'customer';
                const tickStatus = m.read_at ? 'read' : 'sent';
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-none border font-mono text-sm ${
                        mine
                          ? 'bg-[rgba(57,255,20,0.08)] border-[#39FF14] text-[#39FF14]'
                          : 'bg-[#050003] border-[#2A0F3D] text-[#A3C7A8]'
                      }`}
                      data-testid={`customer-chat-msg-${m.id}`}
                    >
                      <MessageBody body={m.body} />
                      <p className={`text-[9px] mt-1 opacity-60 flex items-center gap-1 ${mine ? 'justify-end' : ''}`}>
                        {formatShortTime(m.created_at)}
                        {mine && <Ticks status={tickStatus} />}
                      </p>
                    </div>
                  </div>
                );
              })}
              {showTyping && (
                <div className="flex justify-start" data-testid="customer-chat-typing-indicator">
                  <div className="bg-[#050003] border border-[#2A0F3D] px-3 py-2 rounded-none">
                    <p className="text-[#A3C7A8] font-mono text-xs">
                      Ash &amp; Merch is typing<span className="inline-block animate-pulse ml-1">…</span>
                    </p>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="p-3 border-t border-[#2A0F3D] flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => { setDraft(e.target.value); notifyTyping(); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Type a message…"
                rows={2}
                maxLength={2000}
                className="flex-1 bg-[#050003] border border-[#2A0F3D] text-[#39FF14] font-mono text-sm p-2 rounded-none resize-none focus:border-[#39FF14] focus:outline-none placeholder:text-[#4B6B52]"
                data-testid="customer-chat-input"
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || !draft.trim()}
                className="bg-[#39FF14] text-[#050003] p-2 hover:bg-[#5FFF3F] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="customer-chat-send"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
